import { useCallback } from 'react';

import { ChevronRightIcon, InfoIcon, PlusIcon, SendIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { IntegrationIcon } from '@/components/organisms/Integrations';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { IntegrationType } from '@/shared/types/integration';
import { EventName, PageName } from '@/shared/types/segment';

import { IntegrationSelectContent } from '../../components/BuildSteps/IntegrationSelectContent';
import { BuildPageLoadingState } from '../../components/CampaignLoadingState';
import { useRegisterWizardStep } from '../../hooks/useWizardStepController';
import { CampaignGuidelines } from '../payment-recovery/CampaignGuidelines';
import { useBuildDebtCampaignLogic } from '../payment-recovery/useBuildDebtCampaignLogic';

/**
 * Step 2b: NBA messaging (autonomous actions ON)
 * Adapts from payment-recovery Build with unified navigation
 */
export const MessagingNBA = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const campaignId = useSelector(selectCampaignId);
  const {
    selectedChannels,
    guidelinesInitialMarkdown,
    validationErrors,
    isLoadingEssentialData,
    allIntegrations,
    loadingIntegrations,
    firstIntegration,
    messagesCardSubtitle,
    handleGuidelinesChange,
    handleChannelChange,
    handleAddChannel,
    handleRemoveChannel,
    clearValidationError,
    saveSenderData,
    handleSaveAndExit,
  } = useBuildDebtCampaignLogic();

  useSegments(PageName.CAMPAIGN_MESSAGING);

  // Forward gate for the wizard: track + persist, return whether we may advance.
  const validateMessaging = useCallback(async (): Promise<boolean> => {
    trackEvent(EventName.CampaignMessagingContinue, {
      campaign_id: campaignId,
      campaign_type: 'payment_recovery',
      channel_type: selectedChannels[0] || '',
      has_fallback: selectedChannels.length > 1,
    });
    return saveSenderData(true);
  }, [campaignId, selectedChannels, saveSenderData]);

  const handleTrackedSaveAndExit = useCallback(() => {
    trackEvent(EventName.CampaignMessagingSaveAndExit, {
      campaign_id: campaignId,
      campaign_type: 'payment_recovery',
    });
    handleSaveAndExit();
  }, [campaignId, handleSaveAndExit]);

  const handleTrackedAddChannel = useCallback(() => {
    trackEvent(EventName.CampaignAddChannel, { campaign_id: campaignId });
    handleAddChannel();
  }, [campaignId, handleAddChannel]);

  const navigateToMessagingConfig = () => {
    trackEvent(EventName.CampaignConfigureNBA, { campaign_id: campaignId });
    navigate(`/${slug}/campaigns/${campaignId}/messaging/configure`);
  };

  useRegisterWizardStep(
    { validate: validateMessaging, saveAndExit: handleTrackedSaveAndExit },
    isLoadingEssentialData
  );

  if (isLoadingEssentialData) {
    return <BuildPageLoadingState />;
  }

  return (
    <>
      <div className="rounded-xl bg-muted p-1">
        <div className="flex gap-4">
          {/* Left column - Channels & Messages */}
          <Card className="flex-1 rounded-lg border bg-background py-8 text-foreground shadow-none">
            <CardContent className="space-y-6 px-8">
              {/* Channels Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    className={cn(
                      'text-sm font-medium',
                      validationErrors.channel && 'text-warning'
                    )}
                  >
                    {t('campaigns.builddebtcampaign.channels')}
                  </Label>
                  {selectedChannels[0] && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleTrackedAddChannel}
                      className="h-auto p-0 text-primary hover:bg-transparent"
                    >
                      <PlusIcon className="size-4" />
                      {t('campaigns.builddebtcampaign.add_channel')}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {selectedChannels.map((channelId, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className="flex items-center gap-2"
                    >
                      <div className="flex-1">
                        <IntegrationSelectContent
                          integrations={allIntegrations}
                          loading={loadingIntegrations}
                          value={channelId}
                          onValueChange={(value) => {
                            handleChannelChange(index, value);
                            if (index === 0 && validationErrors.channel) {
                              clearValidationError('channel');
                            }
                          }}
                        />
                      </div>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost-neutral"
                          size="icon-sm"
                          onClick={() => {
                            trackEvent(EventName.CampaignRemoveChannel, {
                              campaign_id: campaignId,
                            });
                            handleRemoveChannel(index);
                          }}
                          className="shrink-0"
                        >
                          <XIcon className="size-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {validationErrors.channel && (
                  <p className="text-sm text-warning">{validationErrors.channel}</p>
                )}
              </div>

              {/* Messages Card */}
              <div className="space-y-2">
                <Card
                  className={cn(
                    'border bg-background py-0 text-foreground shadow-none transition-colors',
                    selectedChannels[0] ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                    validationErrors.firstMessage && 'border-warning'
                  )}
                  onClick={selectedChannels[0] ? navigateToMessagingConfig : undefined}
                >
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="rounded bg-subtle p-2">
                      <div className="rounded bg-background p-1 shadow-sm">
                        {firstIntegration ? (
                          <IntegrationIcon
                            type={firstIntegration.type as IntegrationType}
                            className="size-6"
                          />
                        ) : (
                          <SendIcon className="size-6 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 pr-8">
                      <p
                        className={cn(
                          'text-sm leading-none font-medium',
                          validationErrors.firstMessage && 'text-warning'
                        )}
                      >
                        {t('campaigns.build.initial_message')}
                      </p>
                      <p
                        className={cn(
                          'text-sm leading-5',
                          validationErrors.firstMessage ? 'text-warning' : 'text-muted-foreground'
                        )}
                      >
                        {messagesCardSubtitle}
                      </p>
                    </div>
                    <ChevronRightIcon className="size-6 text-muted-foreground" />
                  </CardContent>
                </Card>
                {validationErrors.firstMessage && (
                  <p className="text-sm text-warning">{validationErrors.firstMessage}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right column - Campaign Guidelines */}
          <CampaignGuidelines
            initialMarkdown={guidelinesInitialMarkdown}
            onChange={handleGuidelinesChange}
          />
        </div>
      </div>

      {/* Info Alert */}
      <div className="mt-4 flex items-center gap-2 rounded-lg p-4">
        <InfoIcon className="size-4 text-muted-foreground" />
        <p className="text-base text-muted-foreground">
          {t('campaigns.builddebtcampaign.info_alert')}
        </p>
      </div>
    </>
  );
};
